import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table
} from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowRight, faArrowLeft, faSync, faPlus } from '@fortawesome/free-solid-svg-icons';
import { API } from '../../config/api';
import { formatProductLabel, formatSectionSpec } from '../../helpers/productLabel';

// The "+ 16" is part of the cable's identity, not an annotation: a 3x25 + 16 and
// a 3x25 are different cables with different bills of material. Every other
// screen says so through cableLabel; this one built its own label out of two
// columns and dropped the green-yellow entirely, so a mixed-section cable was
// indistinguishable from the plain one in the list you pick from.
//
// Kept as a section-only string because the table has separate Conductors and
// Section columns; the row still reads "3" + "25 + 16 mm2" left to right.
const sectionText = std => `${formatSectionSpec(std?.section, std?.greenYellowSection)} mm²`;

const STANDARD_API = `${API.BACKEND}/api/v1/standard`;
const SYNC_API = `${API.REDIS}/api/redis/factory-sync`;

const PHASE_LABELS = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-Stranding',
  conductor_isolation: 'Conductor Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const PHASE_ORDER = [
  'unwinding', 'pre_stranding', 'conductor_isolation', 'rope',
  'interior_sheat', 'armouring', 'exterior_sheat', 'coiling', 'rewinding'
];

const COLOR_CSS = {
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  BLACK: '#222',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  YELLOW: '#f0c400',
  RED: '#d9534f',
  WHITE: '#ccc',
  ORANGE: '#f39c12'
};

const sortPhases = (phases) =>
  [...phases].sort((a, b) => {
    const ia = PHASE_ORDER.indexOf(a.name || a.phase || a);
    const ib = PHASE_ORDER.indexOf(b.name || b.phase || b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

const FactorySync = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 1: Standard selection
  const [cableNames, setCableNames] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [standards, setStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [loadingStandards, setLoadingStandards] = useState(false);

  // Step 2: Drums (multiple lengths)
  const [drums, setDrums] = useState([{ id: 1, length: 5000 }]);
  const nextDrumId = () => Math.max(0, ...drums.map(d => d.id)) + 1;

  // Step 3: Phase status + optional coiling/rewinding
  const [phaseStatuses, setPhaseStatuses] = useState({});
  const [addCoiling, setAddCoiling] = useState(false);
  const [addRewinding, setAddRewinding] = useState(false);

  // Load cable names on mount
  useEffect(() => {
    axios.get(`${STANDARD_API}?name=all`)
      .then(res => {
        const stds = res.data?.data || res.data || [];
        const names = [...new Set(stds.map(s => s.name).filter(Boolean))].sort();
        setCableNames(names);
      })
      .catch(err => console.error('Failed to load standards:', err));
  }, []);

  // Load standards when name selected
  useEffect(() => {
    if (!selectedName) { setStandards([]); return; }
    setLoadingStandards(true);
    axios.get(`${STANDARD_API}?name=${selectedName}`)
      .then(res => {
        const stds = res.data?.data || res.data || [];
        setStandards(stds);
      })
      .catch(err => console.error('Failed to load standards:', err))
      .finally(() => setLoadingStandards(false));
  }, [selectedName]);

  const hasCoilingAlready = (selectedStandard?.phases || []).some(p => p.name === 'coiling');
  const hasRewindingAlready = (selectedStandard?.phases || []).some(p => p.name === 'rewinding');

  // When standard selected, init phase statuses
  const handleSelectStandard = (std) => {
    setSelectedStandard(std);
    setAddCoiling(false);
    setAddRewinding(false);

    const phases = std.phases || [];
    const uniquePhases = [...new Set(phases.map(p => p.name || p.phase).filter(Boolean))];
    const statuses = {};
    uniquePhases.forEach(p => { statuses[p] = 'PENDING'; });
    setPhaseStatuses(statuses);
  };

  // Add/remove coiling from phaseStatuses
  const toggleCoiling = () => {
    if (addCoiling) {
      setAddCoiling(false);
      setPhaseStatuses(prev => {
        const next = { ...prev };
        delete next.coiling;
        return next;
      });
    } else {
      setAddCoiling(true);
      setAddRewinding(false);
      setPhaseStatuses(prev => {
        const next = { ...prev };
        delete next.rewinding;
        next.coiling = 'PENDING';
        return next;
      });
    }
  };

  // Add/remove rewinding from phaseStatuses
  const toggleRewinding = () => {
    if (addRewinding) {
      setAddRewinding(false);
      setPhaseStatuses(prev => {
        const next = { ...prev };
        delete next.rewinding;
        return next;
      });
    } else {
      setAddRewinding(true);
      setAddCoiling(false);
      setPhaseStatuses(prev => {
        const next = { ...prev };
        delete next.coiling;
        next.rewinding = 'PENDING';
        return next;
      });
    }
  };

  // Build enriched phases for submit
  const buildEnrichedPhases = (std) => {
    const phases = (std.phases || []).map(p => ({ ...p }));

    if (addCoiling && !hasCoilingAlready) {
      const productionPhases = phases.filter(p => p.name !== 'coiling' && p.name !== 'rewinding');
      const lastPhase = productionPhases[productionPhases.length - 1];
      if (lastPhase) {
        const lastIdx = phases.findIndex(p => p.name === lastPhase.name);
        phases.splice(lastIdx + 1, 0, {
          name: 'coiling',
          dependsOn: lastPhase.name,
          execution: []
        });
      }
    }

    if (addRewinding && !hasRewindingAlready) {
      const productionPhases = phases.filter(p => p.name !== 'coiling' && p.name !== 'rewinding');
      const lastPhase = productionPhases[productionPhases.length - 1];
      if (lastPhase) {
        const lastIdx = phases.findIndex(p => p.name === lastPhase.name);
        phases.splice(lastIdx + 1, 0, {
          name: 'rewinding',
          dependsOn: lastPhase.name,
          execution: []
        });
      }
    }

    return phases;
  };

  // Submit
  const handleSubmit = async () => {
    setError(null);

    const validDrums = drums.filter(d => Number(d.length) > 0);
    if (!validDrums.length) {
      setError('Add at least one drum with a valid length');
      return;
    }

    setLoading(true);

    const completedPhases = Object.entries(phaseStatuses)
      .filter(([, status]) => status === 'COMPLETED')
      .map(([name]) => name);

    try {
      const enrichedPhases = buildEnrichedPhases(selectedStandard);

      // one standardData entry per drum → creates one job per drum
      const standardData = validDrums.map(d => ({
        ...selectedStandard,
        standardId: selectedStandard._id,
        length: Number(d.length),
        phases: enrichedPhases,
      }));

      const res = await axios.post(SYNC_API, {
        standardData,
        completedPhases
      });

      const jobs = res.data?.jobs || [];
      if (jobs.length === 1) {
        navigate(`/apps/project-management/project-details/${jobs[0].jobId}`);
      } else if (jobs.length > 1) {
        navigate('/apps/project-management/project-monitor');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  const phaseNames = Object.keys(phaseStatuses);
  const sortedPhaseNames = sortPhases(phaseNames);
  const completedCount = Object.values(phaseStatuses).filter(s => s === 'COMPLETED').length;

  return (
    <div className="mb-9 mt-0">
      <div className="mb-4">
        <h2 className="mb-1">
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Factory Sync
        </h2>
        <p className="text-700 mb-0">
          Import current factory state — mark which phases are already completed for existing products
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step indicator */}
      <div className="d-flex align-items-center gap-3 mb-4">
        {[1, 2, 3].map(s => (
          <div key={s} className="d-flex align-items-center gap-2">
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center ${
                step >= s ? 'bg-primary text-white' : 'bg-200 text-700'
              }`}
              style={{ width: 32, height: 32, fontSize: 14, fontWeight: 600 }}
            >
              {step > s ? <FontAwesomeIcon icon={faCheck} /> : s}
            </div>
            <span className={`fs-9 ${step >= s ? 'fw-semibold text-900' : 'text-500'}`}>
              {s === 1 ? 'Select Standard' : s === 2 ? 'Set Length' : 'Phase Status'}
            </span>
            {s < 3 && <span className="text-300 mx-2">—</span>}
          </div>
        ))}
      </div>

      {/* Step 1: Select Standard */}
      {step === 1 && (
        <Card>
          <Card.Header><h5 className="mb-0">Select Cable Standard</h5></Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="fw-semibold">Cable Type</Form.Label>
                <Form.Select
                  value={selectedName}
                  onChange={e => { setSelectedName(e.target.value); setSelectedStandard(null); }}
                >
                  <option value="">-- Select cable type --</option>
                  {cableNames.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {loadingStandards && (
              <div className="mt-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading variants...
              </div>
            )}

            {standards.length > 0 && (
              <div className="mt-4">
                <h6 className="mb-2">Select variant ({standards.length} found)</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="fs-9">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Conductors</th>
                        <th>Section</th>
                        <th>Color</th>
                        <th>Phases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...standards].sort((a, b) => {
                        const sA = Number(a.section) || 0;
                        const sB = Number(b.section) || 0;
                        if (sA !== sB) return sA - sB;
                        const cA = Number(a.numberOfConductors) || 0;
                        const cB = Number(b.numberOfConductors) || 0;
                        return cA - cB;
                      }).map(std => {
                        const isSelected = selectedStandard?._id === std._id;
                        const phaseCount = [...new Set(
                          (std.phases || []).map(p => p.name || p.phase).filter(Boolean)
                        )].length;

                        return (
                          <tr
                            key={std._id}
                            className={isSelected ? 'bg-primary-100' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectStandard(std)}
                          >
                            <td>
                              <Form.Check
                                type="radio"
                                checked={isSelected}
                                onChange={() => handleSelectStandard(std)}
                              />
                            </td>
                            <td className="fw-semibold">{std.name}</td>
                            <td>{std.numberOfConductors}</td>
                            <td>{sectionText(std)}</td>
                            <td>
                              {std.color ? (
                                <span
                                  className="px-2 py-1 rounded-2 text-uppercase fw-semibold"
                                  style={{
                                    fontSize: 11,
                                    border: '1px solid ' + (COLOR_CSS[std.color.toUpperCase()] || '#999'),
                                    color: std.color.toUpperCase() === 'WHITE' ? '#333' : (COLOR_CSS[std.color.toUpperCase()] || '#555'),
                                    background: (COLOR_CSS[std.color.toUpperCase()] || '#999') + '18'
                                  }}
                                >
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      background: COLOR_CSS[std.color.toUpperCase()] || '#999',
                                      marginRight: 6,
                                      verticalAlign: 'middle',
                                      border: '1px solid #0002'
                                    }}
                                  />
                                  {std.color}
                                </span>
                              ) : '-'}
                            </td>
                            <td>{phaseCount} phases</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="primary"
                disabled={!selectedStandard}
                onClick={() => setStep(2)}
              >
                Next <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Step 2: Drums */}
      {step === 2 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Add Drums — {formatProductLabel(selectedStandard || {})}
              {selectedStandard?.color && ` ${selectedStandard.color}`}
            </h5>
          </Card.Header>
          <Card.Body>
            <p className="text-700 mb-3">
              Add one or more drums. Each drum will create a separate job.
            </p>

            {drums.map((drum, idx) => (
              <Row key={drum.id} className="g-2 mb-2 align-items-center">
                <Col xs="auto">
                  <span className="fw-semibold text-700 fs-9" style={{ width: 70, display: 'inline-block' }}>
                    Drum {idx + 1}
                  </span>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="number"
                    size="sm"
                    placeholder="Length (m)"
                    value={drum.length}
                    onChange={e => {
                      setDrums(prev => prev.map(d =>
                        d.id === drum.id ? { ...d, length: e.target.value } : d
                      ));
                    }}
                    min={1}
                  />
                </Col>
                <Col xs="auto">
                  <span className="text-700 fs-9">m</span>
                </Col>
                {drums.length > 1 && (
                  <Col xs="auto">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => setDrums(prev => prev.filter(d => d.id !== drum.id))}
                    >
                      Remove
                    </Button>
                  </Col>
                )}
              </Row>
            ))}

            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2"
              onClick={() => setDrums(prev => [...prev, { id: nextDrumId(), length: '' }])}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add drum
            </Button>

            <div className="mt-3 text-700 fs-9">
              {drums.filter(d => Number(d.length) > 0).length} drum(s),{' '}
              total: {drums.reduce((s, d) => s + Number(d.length || 0), 0).toLocaleString()} m
            </div>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="link" className="text-600" onClick={() => setStep(1)}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
              </Button>
              <Button
                variant="primary"
                disabled={!drums.some(d => Number(d.length) > 0)}
                onClick={() => setStep(3)}
              >
                Next <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Step 3: Phase Status */}
      {step === 3 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Mark Completed Phases — {formatProductLabel(selectedStandard || {})}
              {selectedStandard?.color && ` ${selectedStandard.color}`}
              {' '}({drums.filter(d => Number(d.length) > 0).length} drum{drums.filter(d => Number(d.length) > 0).length !== 1 ? 's' : ''},{' '}
              {drums.reduce((s, d) => s + Number(d.length || 0), 0).toLocaleString()}m total)
            </h5>
          </Card.Header>
          <Card.Body>
            <p className="text-700 mb-3">
              Select which manufacturing phases are already completed for this product.
              Remaining phases can be scheduled later via Plan Next Phase.
            </p>

            {/* Add extra phases: Coiling / Rewinding */}
            {(!hasCoilingAlready || !hasRewindingAlready) && (
              <div className="mb-4 p-3 rounded-2 bg-100 border border-200">
                <span className="fw-semibold fs-9 text-800 d-block mb-2">Add extra phase</span>
                <div className="d-flex gap-2">
                  {!hasCoilingAlready && (
                    <Button
                      variant={addCoiling ? 'primary' : 'outline-secondary'}
                      size="sm"
                      onClick={toggleCoiling}
                    >
                      <FontAwesomeIcon icon={addCoiling ? faCheck : faPlus} className="me-1" />
                      Coiling
                    </Button>
                  )}
                  {!hasRewindingAlready && (
                    <Button
                      variant={addRewinding ? 'primary' : 'outline-secondary'}
                      size="sm"
                      onClick={toggleRewinding}
                    >
                      <FontAwesomeIcon icon={addRewinding ? faCheck : faPlus} className="me-1" />
                      Rewinding
                    </Button>
                  )}
                </div>

                {addCoiling && (
                  <div className="mt-2 text-700 fs-9">
                    Se va configura din Plan Next Phase wizard dupa creare.
                  </div>
                )}
                {addRewinding && (
                  <div className="mt-2 text-700 fs-9">
                    Se va configura din Plan Next Phase wizard dupa creare.
                  </div>
                )}
              </div>
            )}

            <div className="d-flex flex-column gap-2">
              {sortedPhaseNames.map((phaseName, idx) => {
                const status = phaseStatuses[phaseName];
                const isCompleted = status === 'COMPLETED';

                return (
                  <div
                    key={phaseName}
                    className={`d-flex align-items-center justify-content-between p-3 rounded-2 border ${
                      isCompleted ? 'border-success bg-success bg-opacity-10' : 'border-300'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setPhaseStatuses(prev => ({
                        ...prev,
                        [phaseName]: prev[phaseName] === 'COMPLETED' ? 'PENDING' : 'COMPLETED'
                      }));
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center ${
                          isCompleted ? 'bg-success text-white' : 'bg-200 text-700'
                        }`}
                        style={{ width: 28, height: 28, fontSize: 12 }}
                      >
                        {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : idx + 1}
                      </div>
                      <div>
                        <span className="fw-semibold">{PHASE_LABELS[phaseName] || phaseName}</span>
                        <span className="text-700 ms-2 fs-10">({phaseName})</span>
                      </div>
                    </div>

                    <Badge
                      bg={isCompleted ? 'success' : 'secondary'}
                      className="px-3 py-2"
                    >
                      {isCompleted ? 'COMPLETED' : 'PENDING'}
                    </Badge>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 text-700 fs-9">
              {completedCount} of {phaseNames.length} phases marked as completed
            </div>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="link" className="text-600" onClick={() => setStep(2)}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Creating...</>
                ) : (
                  <><FontAwesomeIcon icon={faSync} className="me-1" /> Sync & Create {drums.filter(d => Number(d.length) > 0).length} Job{drums.filter(d => Number(d.length) > 0).length !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default FactorySync;
